// src/app/modules/fournisseurs/FournisseurManagement.jsx

import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";

import {
    fetchFournisseursRequest,
    createFournisseurRequest,
    updateFournisseurRequest,
    deleteFournisseurRequest,
} from "../../../../redux/actions/fournisseursActions";

import {
    fetchConsultantsRequest,
} from "../../../../redux/actions/consultantsActions";

import "../RessourcesStyles/FournisseurManagement.css";

const emptyForm = {
    nom: "",
    raisonSociale: "",
    nomContact: "",
    rc: "",
    reference: "",
    emailContact: "",
    emailCabinet: "",
    specialite: "",
};

const normalize = (v) => String(v || "").trim().toLowerCase();

export default function FournisseurManagement() {
    const dispatch = useDispatch();

    const fournisseursState = useSelector((s) => s.fournisseurs || {});
    const consultantsState = useSelector((s) => s.consultants || {});

    const fournisseurs = fournisseursState.fournisseurs || [];
    const consultants = consultantsState.consultants || [];

    const loading = !!(fournisseursState.loading || consultantsState.loading);

    const [pageTab, setPageTab] = useState("ALL"); // ALL | WITH | WITHOUT
    const [search, setSearch] = useState("");

    const [open, setOpen] = useState(false);
    const [modalTab, setModalTab] = useState("INFOS"); // INFOS | CONSULTANTS
    const [isEdit, setIsEdit] = useState(false);
    const [currentId, setCurrentId] = useState(null);

    const [form, setForm] = useState(emptyForm);
    const [consultSearch, setConsultSearch] = useState("");
    const [selectedConsultantIds, setSelectedConsultantIds] = useState(new Set());

    useEffect(() => {
        dispatch(fetchFournisseursRequest());
        dispatch(fetchConsultantsRequest());
    }, [dispatch]);

    const filteredFournisseurs = useMemo(() => {
        const q = normalize(search);

        return (fournisseurs || []).filter((f) => {
            const list = f?.consultants || [];
            const has = Array.isArray(list) && list.length > 0;

            const okTab =
                pageTab === "ALL" ? true : pageTab === "WITH" ? has : !has;

            const nom = normalize(f?.nom);
            const rs = normalize(f?.raisonSociale);
            const ref = normalize(f?.reference);
            const sp = normalize(f?.specialite);

            const okSearch = !q || nom.includes(q) || rs.includes(q) || ref.includes(q) || sp.includes(q);
            return okTab && okSearch;
        });
    }, [fournisseurs, search, pageTab]);

    const filteredConsultantsForModal = useMemo(() => {
        const q = normalize(consultSearch);
        return (consultants || []).filter((c) => {
            const n = normalize(c?.nomContact);
            const e = normalize(c?.emailContact);
            const s = normalize(c?.specialite);
            return !q || n.includes(q) || e.includes(q) || s.includes(q);
        });
    }, [consultants, consultSearch]);

    const openAdd = () => {
        setIsEdit(false);
        setCurrentId(null);
        setForm(emptyForm);
        setModalTab("INFOS");
        setConsultSearch("");
        setSelectedConsultantIds(new Set());
        setOpen(true);
    };

    const openEdit = (f) => {
        setIsEdit(true);
        setCurrentId(f.id);

        setForm({
            nom: f.nom || "",
            raisonSociale: f.raisonSociale || "",
            nomContact: f.nomContact || "",
            rc: f.rc || "",
            reference: f.reference || "",
            emailContact: f.emailContact || "",
            emailCabinet: f.emailCabinet || "",
            specialite: f.specialite || "",
        });

        const ids = new Set((f?.consultants || []).map((c) => c.id));
        setSelectedConsultantIds(ids);

        setModalTab("INFOS");
        setConsultSearch("");
        setOpen(true);

        // au cas où la liste consultants n'est pas chargée
        if (!consultants?.length) dispatch(fetchConsultantsRequest());
    };

    const closeModal = () => {
        setOpen(false);
        setForm(emptyForm);
        setSelectedConsultantIds(new Set());
        setConsultSearch("");
    };

    const toggleConsultant = (id) => {
        setSelectedConsultantIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const selectAllFiltered = () => {
        setSelectedConsultantIds((prev) => {
            const next = new Set(prev);
            filteredConsultantsForModal.forEach((c) => next.add(c.id));
            return next;
        });
    };

    const clearAll = () =>ToggleAll(false);

    const ToggleAll = (on) => {
        setSelectedConsultantIds((prev) => {
            if (!on) return new Set();
            const next = new Set(prev);
            (consultants || []).forEach((c) => next.add(c.id));
            return next;
        });
    };

    const validate = () => {
        if (!form.nom?.trim()) return "Nom obligatoire";
        return null;
    };

    const onSubmit = () => {
        const err = validate();
        if (err) {
            Swal.fire({ icon: "warning", text: err, timer: 1800, showConfirmButton: false });
            return;
        }

        const payload = {
            nom: form.nom.trim(),
            raisonSociale: form.raisonSociale?.trim() || null,
            nomContact: form.nomContact?.trim() || null,
            rc: form.rc?.trim() || null,
            reference: form.reference?.trim() || null,
            emailContact: form.emailContact?.trim() || null,
            emailCabinet: form.emailCabinet?.trim() || null,
            specialite: form.specialite?.trim() || null,
            consultantIds: Array.from(selectedConsultantIds),
        };

        if (isEdit && currentId) {
            dispatch(updateFournisseurRequest(currentId, payload)); // ✅ signature style "catalogues"
        } else {
            dispatch(createFournisseurRequest(payload));
        }
        closeModal();
    };

    const onDelete = (id) => {
        Swal.fire({
            title: "Supprimer ?",
            text: "Cette action est irréversible",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Oui",
            cancelButtonText: "Annuler",
        }).then((res) => {
            if (res.isConfirmed) dispatch(deleteFournisseurRequest(id));
        });
    };

    return (
        <div className="frnPage">
            <div className="frnHeader">
                <div>
                    <h2>Fournisseurs</h2>
                    <div className="frnSubtitle">Gestion fournisseurs + consultants</div>
                </div>
                <button className="frnBtn frnBtnPrimary" onClick={openAdd}>
                    + Ajouter Fournisseur
                </button>
            </div>

            {/* Tabs page + Search */}
            <div className="frnTabs">
                <button className={`frnTab ${pageTab === "ALL" ? "active" : ""}`} onClick={() => setPageTab("ALL")}>
                    Tous
                </button>
                <button className={`frnTab ${pageTab === "WITH" ? "active" : ""}`} onClick={() => setPageTab("WITH")}>
                    Avec consultants
                </button>
                <button className={`frnTab ${pageTab === "WITHOUT" ? "active" : ""}`} onClick={() => setPageTab("WITHOUT")}>
                    Sans consultants
                </button>

                <div className="frnTabsSpacer" />

                <input
                    className="frnSearch"
                    placeholder="Rechercher (nom / raison / ref / spécialité)"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
            </div>

            <div className="frnCard">
                <div className="frnTableWrap">
                    <table className="frnTable">
                        <thead>
                        <tr>
                            <th>Nom</th>
                            <th>Raison Sociale</th>
                            <th>Email Cabinet</th>
                            <th>Spécialité</th>
                            <th>Consultants</th>
                            <th style={{ width: 180 }}>Actions</th>
                        </tr>
                        </thead>
                        <tbody>
                        {filteredFournisseurs.length === 0 ? (
                            <tr>
                                <td colSpan={6} style={{ textAlign: "center", padding: 18, opacity: 0.7 }}>
                                    Aucun fournisseur
                                </td>
                            </tr>
                        ) : (
                            filteredFournisseurs.map((f) => (
                                <tr key={f.id}>
                                    <td>{f.nom}</td>
                                    <td>{f.raisonSociale || "-"}</td>
                                    <td>{f.emailCabinet || "-"}</td>
                                    <td>{f.specialite || "-"}</td>
                                    <td>
                      <span className="frnBadge">
                        {(f?.consultants || []).length}
                      </span>
                                    </td>
                                    <td>
                                        <div className="frnActions">
                                            <button className="frnBtn frnBtnLight" onClick={() => openEdit(f)}>
                                                Modifier
                                            </button>
                                            <button className="frnBtn frnBtnDanger" onClick={() => onDelete(f.id)}>
                                                Supprimer
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                        </tbody>
                    </table>

                    {loading && <div className="frnLoading">Chargement...</div>}
                </div>
            </div>

            {/* Modal */}
            {open && (
                <div className="frnModal" role="dialog" aria-modal="true" onMouseDown={closeModal}>
                    <div className="frnModalPanel" onMouseDown={(e) => e.stopPropagation()}>
                        <div className="frnModalHead">
                            <h3>{isEdit ? "Modifier Fournisseur" : "Ajouter Fournisseur"}</h3>
                            <button className="frnIconBtn" onClick={closeModal}>✕</button>
                        </div>

                        {/* Modal tabs */}
                        <div className="frnModalTabs">
                            <button
                                className={`frnModalTab ${modalTab === "INFOS" ? "active" : ""}`}
                                onClick={() => setModalTab("INFOS")}
                            >
                                Infos
                            </button>
                            <button
                                className={`frnModalTab ${modalTab === "CONSULTANTS" ? "active" : ""}`}
                                onClick={() => setModalTab("CONSULTANTS")}
                            >
                                Consultants <span className="frnCount">({selectedConsultantIds.size})</span>
                            </button>
                        </div>

                        {modalTab === "INFOS" ? (
                            <div className="frnGrid">
                                <div className="frnField">
                                    <label>Nom *</label>
                                    <input value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} />
                                </div>

                                <div className="frnField">
                                    <label>Raison Sociale</label>
                                    <input value={form.raisonSociale} onChange={(e) => setForm({ ...form, raisonSociale: e.target.value })} />
                                </div>

                                <div className="frnField">
                                    <label>Nom Contact</label>
                                    <input value={form.nomContact} onChange={(e) => setForm({ ...form, nomContact: e.target.value })} />
                                </div>

                                <div className="frnField">
                                    <label>RC</label>
                                    <input value={form.rc} onChange={(e) => setForm({ ...form, rc: e.target.value })} />
                                </div>

                                <div className="frnField">
                                    <label>Référence</label>
                                    <input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} />
                                </div>

                                <div className="frnField">
                                    <label>Spécialité</label>
                                    <input value={form.specialite} onChange={(e) => setForm({ ...form, specialite: e.target.value })} />
                                </div>

                                <div className="frnField">
                                    <label>Email Contact</label>
                                    <input value={form.emailContact} onChange={(e) => setForm({ ...form, emailContact: e.target.value })} />
                                </div>

                                <div className="frnField">
                                    <label>Email Cabinet</label>
                                    <input value={form.emailCabinet} onChange={(e) => setForm({ ...form, emailCabinet: e.target.value })} />
                                </div>
                            </div>
                        ) : (
                            <div className="frnConsultantsBox">
                                <div className="frnConsultantsTop">
                                    <input
                                        className="frnSearch"
                                        placeholder="Rechercher consultant (nom / email / spécialité)"
                                        value={consultSearch}
                                        onChange={(e) => setConsultSearch(e.target.value)}
                                    />
                                    <div className="frnConsultantsBtns">
                                        <button className="frnBtn frnBtnLight" onClick={selectAllFiltered}>
                                            Tout sélectionner (filtré)
                                        </button>
                                        <button className="frnBtn frnBtnLight" onClick={() => ToggleAll(true)}>
                                            Tout sélectionner
                                        </button>
                                        <button className="frnBtn frnBtnLight" onClick={() => ToggleAll(false)}>
                                            Tout désélectionner
                                        </button>
                                    </div>
                                </div>

                                <div className="frnConsultantsList">
                                    {filteredConsultantsForModal.length === 0 ? (
                                        <div className="frnEmpty">Aucun consultant</div>
                                    ) : (
                                        filteredConsultantsForModal.map((c) => {
                                            const checked = selectedConsultantIds.has(c.id);
                                            return (
                                                <label key={c.id} className="frnConsultantRow">
                                                    <input
                                                        type="checkbox"
                                                        checked={checked}
                                                        onChange={() => toggleConsultant(c.id)}
                                                    />
                                                    <div className="frnConsultantInfo">
                                                        <div className="frnConsultantName">
                                                            {c.nomContact}
                                                            {c.emailContact ? <span className="frnMuted"> • {c.emailContact}</span> : null}
                                                        </div>
                                                        <div className="frnMuted">
                                                            {c.specialite || "-"} {c.reference ? ` • Ref: ${c.reference}` : ""}
                                                        </div>
                                                    </div>
                                                </label>
                                            );
                                        })
                                    )}
                                </div>
                            </div>
                        )}

                        <div className="frnModalFoot">
                            <button className="frnBtn frnBtnLight" onClick={closeModal}>
                                Annuler
                            </button>
                            <button className="frnBtn frnBtnPrimary" onClick={onSubmit}>
                                {isEdit ? "Enregistrer" : "Créer"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
